import { useState, useEffect, useMemo } from "react"
import toast from "react-hot-toast"
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core"
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable"
import { useAuth } from "../hooks/useAuth"
import { useEquipments, type Equipment, type EquipmentInput } from "../hooks/useEquipments"
import { useEquipmentIssueStats } from "../hooks/useEquipmentIssueStats"
import { EquipmentFormModal } from "./EquipmentFormModal"
import { EquipmentIssuePanel } from "./EquipmentIssuePanel"
import { SortableRow, SortableCard } from "./EquipmentSortableItems"
import { consumePendingIssueTarget } from "../lib/issueLink"

function formatDate(value?: string) {
  if (!value) return "-"
  const d = new Date(value)
  if (isNaN(d.getTime())) return "-"
  return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}`
}

export function EquipmentHistory() {
  const auth = useAuth()
  const {
    equipments,
    loading,
    fetchEquipments,
    createEquipment,
    updateEquipment,
    updateOrder,
    deleteEquipment,
  } = useEquipments()

  // 장비별 이슈 상태 집계 (실시간 폴링 + 포커스 시 갱신)
  const { statMap, fetchStats, markSeen } = useEquipmentIssueStats()

  const [search, setSearch] = useState("")
  const [showModal, setShowModal] = useState(false)
  const [editTarget, setEditTarget] = useState<Equipment | null>(null)
  const [selectedEquipment, setSelectedEquipment] = useState<Equipment | null>(null)
  // 딥링크로 진입한 대상 이슈 ID (자동 스크롤/하이라이트용)
  const [focusIssueId, setFocusIssueId] = useState<string | null>(null)
  // 드래그 순서 낙관적 반영을 위한 로컬 목록
  const [items, setItems] = useState<Equipment[]>([])

  // 팀장 이상(Manager/C-Level/Admin)만 등록/수정 가능
  const canManage = auth.isAdmin || auth.isManager || auth.isCLevel

  // 드래그 센서 (약간의 이동 후 드래그 시작 → 클릭과 충돌 방지)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  )

  useEffect(() => {
    fetchEquipments()
  }, [fetchEquipments])

  // 훅의 equipments가 갱신되면 로컬 목록 동기화
  useEffect(() => {
    setItems(equipments)
  }, [equipments])

  // 이슈 패널 진입 시 브라우저 history에 항목을 추가하여
  // 뒤로가기 버튼으로 장비 목록으로 복귀할 수 있도록 처리
  const openIssuePanel = (eq: Equipment, issueId?: string) => {
    setSelectedEquipment(eq)
    setFocusIssueId(issueId || null)
    // 해당 장비의 이슈를 확인한 것으로 기록 → 목록의 <신규/변경> 강조 해제
    markSeen(eq._id)
    window.history.pushState({ equipmentIssuePanel: true }, "")
  }

  // 딥링크 소비: 장비 목록 로드 완료 후 pendingIssueTarget이 있으면 해당 장비를 자동 선택
  useEffect(() => {
    if (loading || equipments.length === 0 || selectedEquipment) return
    const target = consumePendingIssueTarget()
    if (!target) return
    const eq = equipments.find((e) => e._id === target.equipmentId)
    if (eq) {
      openIssuePanel(eq, target.issueId)
    } else {
      toast.error("연결된 장비를 찾을 수 없습니다. 삭제되었을 수 있습니다.")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, equipments])

  useEffect(() => {
    const handlePopState = () => {
      // 뒤로가기 시 이슈 패널이 열려 있으면 목록으로 복귀
      setSelectedEquipment(null)
      // 목록 복귀 시 이슈 현황을 즉시 최신화
      fetchStats()
    }
    window.addEventListener("popstate", handlePopState)
    return () => window.removeEventListener("popstate", handlePopState)
  }, [fetchStats])

  const filtered = useMemo(() => {
    const keyword = search.trim().toLowerCase()
    if (!keyword) return items
    return items.filter((e) => {
      const haystack = [
        e.productName,
        e.serialNumber,
        e.supplier,
        e.customerMgmtNumber,
        e.creatorName,
        ...(e.tags || []),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
      return haystack.includes(keyword)
    })
  }, [items, search])

  // 팀장급 이상 + 검색어가 없을 때만 드래그 정렬 허용 (필터 상태에서 인덱스 꼬임 방지)
  const sortEnabled = canManage && search.trim() === ""

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = items.findIndex((e) => e._id === active.id)
    const newIndex = items.findIndex((e) => e._id === over.id)
    if (oldIndex === -1 || newIndex === -1) return

    const reordered = arrayMove(items, oldIndex, newIndex)
    setItems(reordered) // 낙관적 업데이트
    try {
      await updateOrder(reordered.map((e) => e._id))
      toast.success("장비 순서가 변경되었습니다.")
    } catch (error) {
      console.error("장비 순서 변경 실패:", error)
      toast.error("순서 변경 중 오류가 발생했습니다.")
      setItems(equipments) // 실패 시 원복
    }
  }

  const openCreate = () => {
    setEditTarget(null)
    setShowModal(true)
  }

  const openEdit = (eq: Equipment) => {
    setEditTarget(eq)
    setShowModal(true)
  }

  const handleSubmit = async (data: EquipmentInput) => {
    if (editTarget) {
      await updateEquipment(editTarget._id, data)
    } else {
      await createEquipment(data, {
        creator: auth.user?.userId || "",
        creatorName: auth.userName || "",
        creatorEmail: auth.user?.email || "",
      })
    }
  }

  const handleDelete = async (eq: Equipment) => {
    if (!window.confirm(`'${eq.productName}' 장비를 삭제하시겠습니까?`)) return
    try {
      await deleteEquipment(eq._id)
      toast.success("장비가 삭제되었습니다.")
    } catch (error) {
      console.error("장비 삭제 실패:", error)
      toast.error("삭제 중 오류가 발생했습니다.")
    }
  }

  // 이슈 관리 패널 진입
  if (selectedEquipment) {
    return (
      <EquipmentIssuePanel
        equipment={selectedEquipment}
        onBack={() => window.history.back()}
        focusIssueId={focusIssueId}
      />
    )
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      {/* 헤더 */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">장비이력</h1>
          <p className="text-sm text-gray-500 mt-1">납품 장비 목록 및 이력 관리</p>
        </div>
        {canManage && (
          <button
            onClick={openCreate}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-orange-600 to-amber-600 text-white font-semibold rounded-lg hover:from-orange-700 hover:to-amber-700 transition-all shadow-sm"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            장비 등록
          </button>
        )}
      </div>

      {/* 검색 + 카운트 */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="제품명, 시리얼번호, 납품업체, 태그 검색"
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">총 {filtered.length}건</span>
          <button
            onClick={() => {
              fetchEquipments()
              fetchStats()
            }}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            새로고침
          </button>
        </div>
      </div>

      {/* 본문 */}
      {loading ? (
        <div className="flex items-center justify-center py-24">
          <div className="w-10 h-10 border-4 border-orange-200 border-t-orange-600 rounded-full animate-spin"></div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
          <div className="flex flex-col items-center justify-center text-center py-24 px-6">
            <div className="w-16 h-16 bg-orange-50 rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-700 mb-1">
              {search ? "검색 결과가 없습니다" : "등록된 장비가 없습니다"}
            </h2>
            <p className="text-sm text-gray-400">
              {canManage ? "상단의 '장비 등록' 버튼으로 장비를 추가하세요." : "등록된 장비가 표시됩니다."}
            </p>
          </div>
        </div>
      ) : (
        <>
          {canManage && sortEnabled && (
            <p className="text-xs text-gray-400 mb-2">
              왼쪽의 <span className="font-semibold text-gray-500">점 6개</span> 핸들을 드래그하여 장비 순서를 변경할 수 있습니다. (모든 사용자에게 동일하게 반영)
            </p>
          )}
          {canManage && !sortEnabled && search.trim() !== "" && (
            <p className="text-xs text-gray-400 mb-2">
              검색어가 있는 동안에는 순서를 변경할 수 없습니다. 검색어를 지우면 드래그 정렬이 활성화됩니다.
            </p>
          )}

          {/* 데스크톱: 테이블 */}
          <div className="hidden md:block bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-gray-600 text-left">
                      {canManage && <th className="px-2 py-3 w-8"></th>}
                      <th className="px-4 py-3 font-semibold">제품명</th>
                      <th className="px-4 py-3 font-semibold">납품업체</th>
                      <th className="px-4 py-3 font-semibold">고객사 관리번호</th>
                      <th className="px-4 py-3 font-semibold">납품일자</th>
                      <th className="px-4 py-3 font-semibold whitespace-nowrap">이슈 현황</th>
                      <th className="px-4 py-3 font-semibold whitespace-nowrap">등록자</th>
                      {canManage && <th className="px-4 py-3 font-semibold text-right whitespace-nowrap">관리</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    <SortableContext
                      items={filtered.map((e) => e._id)}
                      strategy={verticalListSortingStrategy}
                    >
                      {filtered.map((eq) => (
                        <SortableRow
                          key={eq._id}
                          eq={eq}
                          canManage={canManage}
                          sortEnabled={sortEnabled}
                          stat={statMap[eq._id]}
                          onOpenIssue={openIssuePanel}
                          onEdit={openEdit}
                          onDelete={handleDelete}
                        />
                      ))}
                    </SortableContext>
                  </tbody>
                </table>
              </DndContext>
            </div>
          </div>

          {/* 모바일: 카드 */}
          <div className="md:hidden">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={filtered.map((e) => e._id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-3">
                  {filtered.map((eq) => (
                    <SortableCard
                      key={eq._id}
                      eq={eq}
                      canManage={canManage}
                      sortEnabled={sortEnabled}
                      stat={statMap[eq._id]}
                      onOpenIssue={openIssuePanel}
                      onEdit={openEdit}
                      onDelete={handleDelete}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        </>
      )}

      {showModal && (
        <EquipmentFormModal
          initial={editTarget}
          onClose={() => setShowModal(false)}
          onSubmit={handleSubmit}
        />
      )}
    </div>
  )
}
